import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCandidate, recordCandidateExit, updateFnfStatus } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, User, BookOpen, Briefcase, Phone, Mail,
  MapPin, FileText, Download, CheckCircle,
  XCircle, Clock, Shield, LogOut, X
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusColor = (s) =>
  s === 'active'     ? 'bg-green-50 text-green-700' :
  s === 'deployed'   ? 'bg-blue-50 text-blue-700' :
  s === 'on_bench'   ? 'bg-amber-50 text-amber-700' :
  s === 'blacklisted'? 'bg-red-50 text-red-600' :
  s === 'resigned'   ? 'bg-orange-50 text-orange-600' :
  s === 'terminated' ? 'bg-red-50 text-red-600' :
  'bg-gray-50 text-gray-500';

const categoryColor = (c) =>
  c === 'blue_collar'  ? 'bg-blue-50 text-blue-700' :
  c === 'white_collar' ? 'bg-purple-50 text-purple-700' :
  c === 'specialized'  ? 'bg-teal-50 text-teal-700' :
  'bg-orange-50 text-orange-700';

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-50">
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm font-medium text-gray-800">{value || '—'}</span>
  </div>
);

// ── Record Exit Modal ──────────────────────
const ExitModal = ({ candidateId, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    date_of_resignation : '',
    last_working_date   : '',
    exit_reason         : 'resignation',
    exit_remarks        : '',
    notice_period_served: ''
  });

  const mutation = useMutation({
    mutationFn: (data) => recordCandidateExit(candidateId, data),
    onSuccess: () => {
      toast.success('Exit recorded successfully');
      onSuccess();
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to record exit')
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">Record Employee Exit</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exit Reason *</label>
            <select name="exit_reason" value={form.exit_reason} onChange={handle}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="resignation">Resignation</option>
              <option value="termination">Termination</option>
              <option value="absconding">Absconding</option>
              <option value="contract_end">Contract End</option>
              <option value="retirement">Retirement</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Resignation</label>
              <input type="date" name="date_of_resignation" value={form.date_of_resignation} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Working Date *</label>
              <input type="date" name="last_working_date" value={form.last_working_date} onChange={handle} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period Served (days)</label>
            <input type="number" name="notice_period_served" value={form.notice_period_served} onChange={handle} min="0"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 30" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea name="exit_remarks" value={form.exit_remarks} onChange={handle} rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Additional notes about the exit..." />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Record Exit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CandidateDetail = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const queryClient  = useQueryClient();
  const [tab, setTab]         = useState('profile');
  const [showExit, setShowExit] = useState(false);

  const isAdmin = ['super_admin', 'branch_manager'].includes(user?.role);

  const { data: candidate, isLoading } = useQuery({
    queryKey: ['candidate', id],
    queryFn : () => getCandidate(id).then(r => r.data.data)
  });

  const fnfMutation = useMutation({
    mutationFn: (fnf_status) => updateFnfStatus(id, { fnf_status }),
    onSuccess: () => {
      toast.success('FnF status updated');
      queryClient.invalidateQueries(['candidate', id]);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update FnF status')
  });

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      Loading candidate profile...
    </div>
  );

  if (!candidate) return (
    <div className="text-center py-12 text-gray-400">Candidate not found</div>
  );

  const tabs = [
    { id: 'profile',      label: 'Profile' },
    { id: 'education',    label: 'Education' },
    { id: 'experience',   label: 'Experience' },
    { id: 'documents',    label: 'Documents' },
    { id: 'applications', label: 'Applications' },
    { id: 'exit',         label: 'Exit & FnF' }
  ];

  return (
    <div className="space-y-5">
      {/* Back Button */}
      <button onClick={() => navigate('/candidates')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} />
        Back to Candidates
      </button>

      {/* Profile Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start gap-5">
          {/* Avatar */}
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-purple-600 text-2xl font-bold">
              {candidate.first_name?.charAt(0)}
            </span>
          </div>

          {/* Basic Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-900">
                {candidate.first_name} {candidate.last_name}
              </h1>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(candidate.status)}`}>
                {candidate.status}
              </span>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${categoryColor(candidate.worker_category)}`}>
                {candidate.worker_category?.replace('_', ' ')}
              </span>
            </div>

            <p className="text-sm text-gray-400 mt-1">
              {candidate.employee_code} · Added via {candidate.source || 'recruiter'}
            </p>

            <div className="flex flex-wrap gap-4 mt-3">
              {candidate.phone && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Phone size={14} className="text-gray-400" />
                  {candidate.phone}
                </div>
              )}
              {candidate.email && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Mail size={14} className="text-gray-400" />
                  {candidate.email}
                </div>
              )}
              {candidate.city && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <MapPin size={14} className="text-gray-400" />
                  {candidate.city}, {candidate.state}
                </div>
              )}
            </div>
          </div>

          {/* Record Exit Button */}
          {isAdmin && !['resigned', 'terminated'].includes(candidate.status) && (
            <button onClick={() => setShowExit(true)}
              className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-100 flex-shrink-0">
              <LogOut size={15} />
              Record Exit
            </button>
          )}

          {/* Stats */}
          <div className="flex gap-4 flex-shrink-0">
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">
                {candidate.total_experience || 0}
              </p>
              <p className="text-xs text-gray-400">Yrs Exp</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">
                {candidate._count?.applications || 0}
              </p>
              <p className="text-xs text-gray-400">Applied</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">
                {candidate._count?.deployments || 0}
              </p>
              <p className="text-xs text-gray-400">Deployed</p>
            </div>
          </div>
        </div>

        {/* Profile Score */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Profile Completeness</span>
            <span className="font-medium">{candidate.profile_score}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full"
              style={{ width: `${candidate.profile_score}%` }} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── PROFILE TAB ── */}
      {tab === 'profile' && (
        <div className="grid grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <User size={16} className="text-blue-600" />
              Personal Details
            </h3>
            <InfoRow label="Full Name"       value={`${candidate.first_name} ${candidate.last_name || ''}`} />
            <InfoRow label="Date of Birth"   value={candidate.date_of_birth?.slice(0, 10)} />
            <InfoRow label="Gender"          value={candidate.gender} />
            <InfoRow label="Marital Status"  value={candidate.marital_status} />
            <InfoRow label="Blood Group"     value={candidate.blood_group} />
            <InfoRow label="Nationality"     value={candidate.nationality} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Shield size={16} className="text-green-600" />
              Identity & Compliance
            </h3>
            <InfoRow label="Employee Code"  value={candidate.employee_code} />
            <InfoRow label="Aadhar Number"  value={candidate.aadhar_number ? `XXXX-XXXX-${candidate.aadhar_number.slice(-4)}` : null} />
            <InfoRow label="PAN Number"     value={candidate.pan_number} />
            <InfoRow label="UAN Number"     value={candidate.uan_number} />
            <InfoRow label="ESIC Number"    value={candidate.esic_number} />
            <InfoRow label="Source"         value={candidate.source} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <MapPin size={16} className="text-red-500" />
              Address
            </h3>
            <InfoRow label="Current Address"   value={candidate.address_current} />
            <InfoRow label="Permanent Address" value={candidate.address_permanent} />
            <InfoRow label="City"              value={candidate.city} />
            <InfoRow label="State"             value={candidate.state} />
            <InfoRow label="Pincode"           value={candidate.pincode} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Briefcase size={16} className="text-purple-600" />
              Job Preferences
            </h3>
            <InfoRow label="Category"         value={candidate.worker_category?.replace('_', ' ')} />
            <InfoRow label="Total Experience" value={candidate.total_experience ? `${candidate.total_experience} years` : null} />
            <InfoRow label="Current CTC"      value={candidate.current_ctc ? `₹${Number(candidate.current_ctc).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Expected CTC"     value={candidate.expected_ctc ? `₹${Number(candidate.expected_ctc).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Notice Period"    value={candidate.notice_period ? `${candidate.notice_period} days` : null} />
            <InfoRow label="Available From"   value={candidate.available_from?.slice(0, 10)} />
          </div>
        </div>
      )}

      {/* ── EDUCATION TAB ── */}
      {tab === 'education' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <BookOpen size={16} className="text-blue-600" />
            Education History
          </h3>
          {candidate.candidate_education?.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No education records added</div>
          ) : (
            <div className="space-y-4">
              {candidate.candidate_education?.map(edu => (
                <div key={edu.id} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{edu.degree}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{edu.institution}</p>
                      {edu.board_university && (
                        <p className="text-xs text-gray-400">{edu.board_university}</p>
                      )}
                    </div>
                    <div className="text-right">
                      {edu.year_of_passing && (
                        <p className="text-sm font-medium text-gray-700">{edu.year_of_passing}</p>
                      )}
                      {edu.percentage && (
                        <p className="text-xs text-gray-400">{edu.percentage}%</p>
                      )}
                    </div>
                  </div>
                  {edu.is_highest && (
                    <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full mt-2 inline-block">
                      Highest Qualification
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── EXPERIENCE TAB ── */}
      {tab === 'experience' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Briefcase size={16} className="text-purple-600" />
            Work Experience
          </h3>
          {candidate.candidate_experience?.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No experience records added</div>
          ) : (
            <div className="space-y-4">
              {candidate.candidate_experience?.map(exp => (
                <div key={exp.id} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{exp.company_name}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{exp.designation}</p>
                      {exp.responsibilities && (
                        <p className="text-xs text-gray-400 mt-1">{exp.responsibilities}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-xs text-gray-500">
                        {exp.from_date?.slice(0, 7)} →{' '}
                        {exp.is_current ? 'Present' : exp.to_date?.slice(0, 7)}
                      </p>
                      {exp.last_ctc && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          ₹{Number(exp.last_ctc).toLocaleString('en-IN')}/month
                        </p>
                      )}
                      {exp.reason_for_leaving && (
                        <p className="text-xs text-gray-400 mt-0.5">{exp.reason_for_leaving}</p>
                      )}
                    </div>
                  </div>
                  {exp.is_current && (
                    <span className="text-xs px-2 py-0.5 bg-green-50 text-green-600 rounded-full mt-2 inline-block">
                      Currently Working
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── DOCUMENTS TAB ── */}
      {tab === 'documents' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FileText size={16} className="text-indigo-600" />
            Uploaded Documents
          </h3>
          {candidate.documents?.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No documents uploaded yet</div>
          ) : (
            <div className="space-y-3">
              {candidate.documents?.map(doc => (
                <div key={doc.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center">
                      <FileText size={16} className="text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                      <p className="text-xs text-gray-400 capitalize">
                        {doc.document_type?.replace('_', ' ')} ·{' '}
                        {doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full
                      ${doc.verification_status === 'verified'
                        ? 'bg-green-50 text-green-600'
                        : 'bg-amber-50 text-amber-600'}`}>
                      {doc.verification_status || 'pending'}
                    </span>
                    <a href={`http://localhost:5000${doc.file_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      <Download size={12} />
                      View
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── EXIT & FNF TAB ── */}
      {tab === 'exit' && (
        <div className="space-y-5">
          {/* Exit Details */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <LogOut size={16} className="text-red-500" />
                Exit Details
              </h3>
              {isAdmin && !['resigned', 'terminated'].includes(candidate.status) && (
                <button onClick={() => setShowExit(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium">
                  <LogOut size={13} />
                  Record Exit
                </button>
              )}
            </div>
            {!candidate.last_working_date ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No exit recorded for this employee
              </div>
            ) : (
              <div>
                <InfoRow label="Exit Reason"          value={candidate.exit_reason?.replace('_', ' ')} />
                <InfoRow label="Date of Resignation"  value={candidate.date_of_resignation?.slice(0, 10)} />
                <InfoRow label="Last Working Date"    value={candidate.last_working_date?.slice(0, 10)} />
                <InfoRow label="Notice Period Served" value={candidate.notice_period_served ? `${candidate.notice_period_served} days` : null} />
                <InfoRow label="Remarks"              value={candidate.exit_remarks} />
              </div>
            )}
          </div>

          {/* FnF Status */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <CheckCircle size={16} className="text-green-600" />
              Full & Final Settlement
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">FnF Status</p>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium mt-1 inline-block
                  ${candidate.fnf_status === 'completed' ? 'bg-green-50 text-green-700' :
                    candidate.fnf_status === 'processed' ? 'bg-blue-50 text-blue-700' :
                    'bg-amber-50 text-amber-700'}`}>
                  {candidate.fnf_status || 'pending'}
                </span>
              </div>
              {isAdmin && candidate.last_working_date && (
                <div className="flex gap-2">
                  {candidate.fnf_status !== 'processed' && (
                    <button onClick={() => fnfMutation.mutate('processed')}
                      disabled={fnfMutation.isPending}
                      className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium disabled:opacity-50">
                      Mark Processed
                    </button>
                  )}
                  {candidate.fnf_status !== 'completed' && (
                    <button onClick={() => fnfMutation.mutate('completed')}
                      disabled={fnfMutation.isPending}
                      className="text-xs px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 font-medium disabled:opacity-50">
                      Mark Completed
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── APPLICATIONS TAB ── */}
      {tab === 'applications' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Briefcase size={16} className="text-amber-600" />
            Application History
          </h3>
          {candidate.applications?.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              No applications yet
            </div>
          ) : (
            <div className="space-y-3">
              {candidate.applications?.map(app => (
                <div key={app.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Application #{app.id?.slice(0, 8)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Applied: {app.applied_at?.slice(0, 10)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {app.stage === 'selected'  && <CheckCircle size={14} className="text-green-600" />}
                    {app.stage === 'rejected'  && <XCircle size={14} className="text-red-600" />}
                    {!['selected','rejected'].includes(app.stage) && <Clock size={14} className="text-amber-600" />}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(app.stage)}`}>
                      {app.stage}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {showExit && (
        <ExitModal
          candidateId={id}
          onClose={() => setShowExit(false)}
          onSuccess={() => queryClient.invalidateQueries(['candidate', id])}
        />
      )}
    </div>
  );
};

export default CandidateDetail;